import React, { useEffect, useState } from 'react';
import { Col, Radio, Row, RadioChangeEvent } from 'antd';
import { UsersApi } from 'api/api';
import { UserDto, UserFilterType } from 'api/models';
import { ExportButtonPageHeader } from 'components/buttons/export-button/export-button';
import { Cards } from 'components/cards/frame/cards-frame';
import { useAuthorization } from 'hooks/useAuthorization';
import { useTableSorting } from 'hooks/useTableSorting';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AutoComplete } from '../../components/autoComplete/autoComplete';
import { Button } from '../../components/buttons/buttons';
import { PageHeader } from '../../components/page-headers/page-headers';
import { CardToolbox, Main, TopToolBox, StyledRadioGroup } from '../../container/styled';
import UsersTable from './UsersTable';
import styled from 'styled-components';

const usersApi = new UsersApi();

interface FilterKey {
  id: UserFilterType;
  name: string;
}

interface UsersData {
  items: UserDto[];
  pageNumber: number;
  totalPages: number;
  totalCount: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface RequestParams {
  filterType: UserFilterType;
  search: string;
  pageNumber: number;
  pageSize: number;
}

const UsersPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = useAuthorization();

  const filterKeys: FilterKey[] = [
    { id: UserFilterType.NUMBER_1, name: t("users:select.active", "Active") },
    { id: UserFilterType.NUMBER_2, name: t("users:select.not-confirmed", "Not Confirmed") },
    { id: UserFilterType.NUMBER_3, name: t("users:select.disabled", "Disabled") },
    { id: UserFilterType.NUMBER_4, name: t("users:select.deleted", "Deleted") },
    { id: UserFilterType.NUMBER_5, name: t("users:select.all", "All") },
  ];

  const [usersData, setUsersData] = useState<UsersData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { sorting, onSorterChange } = useTableSorting();

  const [request, setRequest] = useState<RequestParams>({
    filterType: UserFilterType.NUMBER_1,
    search: "",
    pageNumber: 1,
    pageSize: 10,
  });

  useEffect(() => {
    if (request) {
      getUsers();
    }
  }, [request, sorting]);

  const getUsers = async () => {
    try {
      setIsLoading(true);
      const { data } = await usersApi.apiUsersListGet({ ...request, ...sorting });
      setUsersData(data as any);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const onFilterChange = (e: RadioChangeEvent) => {
    setRequest(prev => ({
      ...prev,
      filterType: e.target.value,
      pageNumber: 1,
      pageSize: 10,
    }));
  };

  const onSearchChange = (value: string) => {
    setRequest(prev => ({ ...prev, search: value }));
  };

  const onPaginationChange = (pageNumber: number) => {
    setRequest(prev => ({ ...prev, pageNumber }));
  };

  const onShowSizeChange = (pageNumber: number, pageSize: number) => {
    setRequest(prev => ({ ...prev, pageNumber, pageSize }));
  };

  return (
    <>
      <CardToolbox>
        <PageHeader
          ghost
          title={t("users:users-title", "Users Management")}
          buttons={[
            <div key="__actions" className="page-header-actions">
              <ExportButtonPageHeader 
                key="1" 
                title={t("users:users-title", "Users Management")} 
                data={usersData?.items} 
                columns={['ordinalNumber', 'firstName', 'lastName', 'email', 'roleName']} 
              />
              {hasPermission("users:add") && (
                <Button
                  className="btn-add_new"
                  size="default"
                  type="primary"
                  key="btn_2"
                >
                  <Link to="/users/create">
                    {t("users:button.add", "+ Add New User")}
                  </Link>
                </Button>
              )}
            </div>,
          ]}
        />
      </CardToolbox>

      <Main>
        <Cards headless>
          <Row gutter={15}>
            <Col xs={24}>
              <TopToolBox>
                <Row gutter={15}>
                  <Col lg={6} md={6} xs={24}>
                    <div className="table-search-box">
                      <AutoComplete
                        onSearch={onSearchChange}
                        width="100%"
                        patterns
                      />
                    </div>
                  </Col>
                  <Col lg={12} md={14} xs={24}>
                    <div
                      className="table-toolbox-menu"
                      style={{ float: "left", marginLeft: 20 }}
                    >
                      <span className="toolbox-menu-title" style={{ marginRight: 10 }}>
                        {t("users:lable.status", "Status:")}
                      </span>
                      <StyledRadioGroup
                        onChange={onFilterChange}
                        value={request.filterType}
                      >
                        {filterKeys.map((item) => (
                          <Radio.Button 
                            key={item.id} 
                            value={item.id}
                          >
                            {item.name}
                          </Radio.Button>
                        ))}
                      </StyledRadioGroup>
                    </div>
                  </Col>
                </Row>
              </TopToolBox>
            </Col>
          </Row>
          <Row gutter={0}>
            <UsersTable
              data={usersData}
              isLoading={isLoading}
              onPaginationChange={onPaginationChange}
              onShowSizeChange={onShowSizeChange}
              onSorterChange={onSorterChange}
              refetch={getUsers}
            />
          </Row>
        </Cards>
      </Main>
    </>
  );
};

export default UsersPage;